import React, { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import api from "../lib/api"; 
import { Edit2, Trash2, Save, X, Plus, Clock } from "lucide-react";
import "./ShiftMaster.css";

const ShiftMaster = () => {
  const navigate = useNavigate();
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [newShift, setNewShift] = useState({
    code: "", name: "", start_time: "09:00", end_time: "18:00", scheduled_break: 1.0,
    is_day_off: false, day_category: "work",
  });

  /** 開始・終了とも空（--:--）か。欠勤・休日など時刻を持たないシフト用 */
  const noTime = (s) => !s?.start_time && !s?.end_time;

  /** 「時刻なし」チェックの切り替えで入れる値。外すと既定の 09:00-18:00 に戻す */
  const clearedTimes = (checked) =>
    checked ? { start_time: "", end_time: "" } : { start_time: "09:00", end_time: "18:00" };

  /** 「休み」チェックの切り替えで入れる値。
   *  休みにすると出勤日数・労働時間の対象外になるため、時刻も不要なので合わせて消す。 */
  const dayOffValues = (checked) =>
    checked
      ? { is_day_off: true, day_category: "holiday", start_time: "", end_time: "", scheduled_break: 0 }
      : { is_day_off: false, day_category: "work", start_time: "09:00", end_time: "18:00" };

  /** サーバーが返した理由を読める形にする（例: シフトコードの重複・必須未入力） */
  const errorDetail = (err) => {
    const data = err?.response?.data;
    if (!data) return err?.message || "";
    if (typeof data === "string") return data;
    if (data.detail) return data.detail;
    // {"code": ["この シフトコード を持った シフト管理 が既に存在します。"]} 形式
    const labels = {
      code: "シフトコード",
      name: "シフト名",
      start_time: "勤務開始時間",
      end_time: "勤務終了時間",
      scheduled_break: "休憩時間",
      break_start_time: "休憩開始時刻",
    };
    return Object.entries(data)
      .map(([k, v]) => `${labels[k] || k}: ${Array.isArray(v) ? v.join(" ") : v}`)
      .join("\n");
  };

  // 1. データベースからシフトプリセットを取得
  const fetchShifts = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/shift-presets/");
      setShifts(res.data);
    } catch (err) {
      alert("データをダウンロードできません。");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchShifts(); }, []);

  // 2. ニューシフトを追加
  const handleAddShift = async () => {
    try {
      await api.post("/admin/shift-presets/", newShift);
      setNewShift({ code: "", name: "", start_time: "09:00", end_time: "18:00", scheduled_break: 1.0 });
      fetchShifts();
    } catch (err) {
      const why = errorDetail(err);
      alert(why ? `追加できませんでした。\n\n${why}` : "データを追加できません。");
    }
  };

  // 3. シフトを削除
  const handleDelete = async (id) => {
    if (!window.confirm("データを削除してもよろしいですか？")) return;
    try {
      await api.delete(`/admin/shift-presets/${id}/`);
      fetchShifts();
    } catch (err) {
      const why = errorDetail(err);
      alert(why ? `削除に失敗しました。\n\n${why}` : "削除に失敗しました。");
    }
  };

  // 4. チェック編集
  const handleSaveEdit = async (id) => {
    try {
      await api.put(`/admin/shift-presets/${id}/`, editForm);
      setEditingId(null);
      fetchShifts();
    } catch (err) {
      const why = errorDetail(err);
      alert(why ? `保存に失敗しました。\n\n${why}` : "保存に失敗しました。");
    }
  };

  // CSVエクスポート
  const handleExportCsv = async () => {
    try {
      const res = await api.get("/admin/shift-presets/export-csv/", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "shift_presets.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      alert("CSVの書き出しに失敗しました。");
    }
  };

  // CSVインポート（シフトコードでupsert）
  const handleImportCsv = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!window.confirm("CSVを取り込みます。シフトコードが一致すれば更新、無ければ新規追加します。よろしいですか？")) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/admin/shift-presets/import-csv/", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const { created = 0, updated = 0, errors = [] } = res.data || {};
      let msg = `取込完了：新規 ${created} 件、更新 ${updated} 件。`;
      if (errors.length) msg += `\n警告 ${errors.length} 件:\n` + errors.slice(0, 20).join("\n");
      alert(msg);
      fetchShifts();
    } catch (err) {
      alert(err.response?.data?.detail || "CSVの取り込みに失敗しました。");
    }
  };

  return (
    <div className="shift-master-container">
      <div className="shift-header">
        <h2 className="shift-title"><Clock size={24} /> シフト管理マスター</h2>
        <div style={{ display: "flex", gap: 8 }}>
          <label className="btn-add" style={{ cursor: "pointer" }}>
            CSV取込
            <input type="file" accept=".csv,text/csv" style={{ display: "none" }} onChange={handleImportCsv} />
          </label>
          <button className="btn-add" onClick={handleExportCsv}>CSVエクスポート</button>
          <button className="lm-btn-back" onClick={() => navigate(-1)}>戻る</button>
        </div>
      </div>
      {/* Form　追加 */}
      <div className="shift-add-card">
        <h3><Plus size={18} /> シフトを追加</h3>
        <div className="shift-form-grid">
          <input placeholder="コード" value={newShift.code} onChange={e => setNewShift({...newShift, code: e.target.value})} />
          <input placeholder="シフト名" value={newShift.name} onChange={e => setNewShift({...newShift, name: e.target.value})} />
          <input
            type="time"
            value={newShift.start_time}
            disabled={noTime(newShift)}
            onChange={e => setNewShift({...newShift, start_time: e.target.value})}
          />
          <input
            type="time"
            value={newShift.end_time}
            disabled={noTime(newShift)}
            onChange={e => setNewShift({...newShift, end_time: e.target.value})}
          />
          <input type="number" step="0.25" placeholder="休憩時間 (例: 0.75)" value={newShift.scheduled_break} onChange={e => setNewShift({...newShift, scheduled_break: e.target.value})} />
          <button onClick={handleAddShift} className="btn-add">追加</button>
        </div>
        <div className="shift-add-options">
          {/* 欠勤・休日など時刻を持たないシフト用。チェックすると開始・終了を「--:--」にする */}
          <label className="shift-no-time-label">
            <input
              type="checkbox"
              checked={noTime(newShift)}
              onChange={e => setNewShift({ ...newShift, ...clearedTimes(e.target.checked) })}
            />
            時刻なし（--:--）
          </label>
          {/* 欠勤・休日など、出勤日数や労働時間に数えないシフト */}
          <label className="shift-no-time-label">
            <input
              type="checkbox"
              checked={!!newShift.is_day_off}
              onChange={e => setNewShift({ ...newShift, ...dayOffValues(e.target.checked) })}
            />
            休み（欠勤・休日。出勤日数と労働時間に数えない）
          </label>
        </div>
      </div>

      {/* テーブル */}
      <div className="shift-table-wrapper">
        <table className="shift-table">
          <thead>
            <tr>
              <th>シフトコード</th>
              <th>シフト名</th>
              <th>開始時間</th>
              <th>終了時間</th>
              <th>休憩時間</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {shifts.map(s => (
              <tr key={s.id}>
                {editingId === s.id ? (
                  <>
                    <td><input value={editForm.code} onChange={e => setEditForm({...editForm, code: e.target.value})} /></td>
                    <td><input value={editForm.name} onChange={e => setEditForm({...editForm, name: e.target.value})} /></td>
                    <td>
                      <input
                        type="time"
                        value={editForm.start_time || ""}
                        disabled={noTime(editForm)}
                        onChange={e => setEditForm({...editForm, start_time: e.target.value})}
                      />
                      {/* 時刻なし（--:--）／休み（出勤日数・労働時間に数えない）に切り替える */}
                      <label className="shift-no-time-label">
                        <input
                          type="checkbox"
                          checked={noTime(editForm)}
                          onChange={e => setEditForm({ ...editForm, ...clearedTimes(e.target.checked) })}
                        />
                        時刻なし
                      </label>
                      <label className="shift-no-time-label">
                        <input
                          type="checkbox"
                          checked={!!editForm.is_day_off}
                          onChange={e => setEditForm({ ...editForm, ...dayOffValues(e.target.checked) })}
                        />
                        休み
                      </label>
                    </td>
                    <td>
                      <input
                        type="time"
                        value={editForm.end_time || ""}
                        disabled={noTime(editForm)}
                        onChange={e => setEditForm({...editForm, end_time: e.target.value})}
                      />
                    </td>
                    <td><input type="number" step="0.25" value={editForm.scheduled_break || 0} onChange={e => setEditForm({...editForm, scheduled_break: e.target.value})} /></td>
                    <td className="actions">
                      <button onClick={() => handleSaveEdit(s.id)} className="btn-save"><Save size={16} /></button>
                      <button onClick={() => setEditingId(null)} className="btn-cancel"><X size={16} /></button>
                    </td>
                  </>
                ) : (
                  <>
                    <td>{s.code}</td>
                    <td>{s.name}</td>
                    {/* 時刻なしのシフト（欠勤・休日など）は「--:--」と表示する */}
                    <td>{s.start_time ? s.start_time.slice(0, 5) : "--:--"}</td>
                    <td>{s.end_time ? s.end_time.slice(0, 5) : "--:--"}</td>
                    <td>{s.scheduled_break}</td>
                    <td className="actions">
                      <button onClick={() => { setEditingId(s.id); setEditForm(s); }} className="btn-edit"><Edit2 size={16} /></button>
                      <button onClick={() => handleDelete(s.id)} className="btn-delete"><Trash2 size={16} /></button>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ShiftMaster;